import {useTranslation} from "react-i18next";
import {useNavigate, useParams, useSearchParams} from "react-router";
import {
    Typography, TextField, Select, MenuItem, FormControl, InputLabel,
    Radio, RadioGroup, FormControlLabel, Checkbox,
    Grid, Button, FormHelperText, Autocomplete, Box,
} from "@mui/material";
import {Controller} from "react-hook-form";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import {ROUTES} from "../../router/config";
import {TITLES_OPTIONS} from "../../constants/titles";
import {SUFFIXES_OPTIONS} from "../../constants/suffix";
import {useGetCountries} from "../../hooks/useGetCountries";
import Preloader from "../../components/Preloader";
import {useLeadRequest} from "../../hooks/useLeadRequest";
import {useApplicationStorage} from "../../hooks/useApplicationStorage";
import {useParentDetailsForm} from "./hooks/useParentDetailsForm";

const ParentPage = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {applicationId} = useParams();
    const [searchParams] = useSearchParams();

    const isEdit = searchParams.has("edit");

    const {leadData, setLead} = useApplicationStorage();

    const {putLeadRequest, isPendingLeadRequest} = useLeadRequest(applicationId);
    const {isLoading: isLoadingCountries, phoneCodeOnlyOptions} = useGetCountries();

    const {
        handleSubmit, control, errors,
        isDirty,
    } = useParentDetailsForm(leadData);

    const onSubmit = async (data) => {
        try {
            if (isDirty) {
                const bodyData = {...data, onlineRegistrationId: applicationId};
                if (leadData?.leadId) bodyData.leadId = leadData.leadId;

                const response = await putLeadRequest(bodyData);

                setLead({...bodyData, leadId: response.leadId});
            }

            navigate(`/${applicationId}/${ROUTES.ADDRESS}${isEdit ? "?edit" : ""}`);
        } catch {
            // fail silently
        }
    };

    const handleBack = () => {
        navigate(`/${applicationId}/${ROUTES.CONFIRMATION}`);
    };

    return (
        <div className="ParentPage">
            <Typography variant="h1">
                {t("parent.title")}
            </Typography>
            <Typography variant="body1" className="mt-4" gutterBottom>
                {t("parent.subtitle")}
            </Typography>
            <Preloader isLoading={isLoadingCountries} overflow>
                <form className="ParentPage-form mt-12" onSubmit={handleSubmit(onSubmit)}>
                    <Grid container rowSpacing={3} columnSpacing={2}>
                        <Grid item size={{xs: 12, md: 2}}>
                            <FormControl fullWidth error={!!errors.title}>
                                <InputLabel required>{t("parent.titleLabel")}</InputLabel>
                                <Controller
                                    name="title"
                                    control={control}
                                    render={({field}) => (
                                        <Select {...field}>
                                            {TITLES_OPTIONS.map((title) => (
                                                <MenuItem key={title.value} value={title.value}>
                                                    {title.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                />
                                {errors.title?.message ? (
                                    <FormHelperText>{errors.title.message}</FormHelperText>
                                ) : null}
                            </FormControl>
                        </Grid>
                        <Grid item size={{xs: 12, md: 4}}>
                            <Controller
                                name="forename"
                                control={control}
                                render={({field}) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        slotProps={{htmlInput: {maxLength: 100, required: false}}}
                                        label={t("parent.legalFirstName")}
                                        placeholder={t("parent.placeholders.firstName")}
                                        required
                                        error={!!errors.forename}
                                        helperText={errors.forename?.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item size={{xs: 12, md: 4}}>
                            <Controller
                                name="surname"
                                control={control}
                                render={({field}) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        slotProps={{htmlInput: {maxLength: 100, required: false}}}
                                        label={t("parent.legalLastName")}
                                        placeholder={t("parent.placeholders.lastName")}
                                        required
                                        error={!!errors.surname}
                                        helperText={errors.surname?.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item size={{xs: 12, md: 2}}>
                            <FormControl fullWidth error={!!errors.suffix}>
                                <InputLabel>{t("parent.suffix")}</InputLabel>
                                <Controller
                                    name="suffix"
                                    control={control}
                                    render={({field}) => (
                                        <Select {...field}>
                                            <MenuItem value="">
                                                <em className="select-placeholder">{t("parent.placeholders.suffix")}</em>
                                            </MenuItem>
                                            {SUFFIXES_OPTIONS.map((suffix) => (
                                                <MenuItem key={suffix.value} value={suffix.value}>
                                                    {suffix.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                />
                                {errors.suffix?.message ? (
                                    <FormHelperText>{errors.suffix.message}</FormHelperText>
                                ) : null}
                            </FormControl>
                        </Grid>

                        <Grid item size={{xs: 12, md: 6}}>
                            <Controller
                                name="emailAddress"
                                control={control}
                                render={({field}) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        slotProps={{htmlInput: {required: false}}}
                                        type="email"
                                        label={t("parent.emailAddress")}
                                        placeholder={t("parent.placeholders.email")}
                                        required
                                        error={!!errors.emailAddress}
                                        helperText={errors.emailAddress?.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item size={{xs: 12, md: 6}}>
                            <Controller
                                name="confirmEmailAddress"
                                control={control}
                                render={({field}) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        slotProps={{htmlInput: {required: false}}}
                                        type="email"
                                        label={t("parent.confirmEmailAddress")}
                                        placeholder={t("parent.placeholders.email")}
                                        required
                                        error={!!errors.confirmEmailAddress}
                                        helperText={errors.confirmEmailAddress?.message}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item size={{xs: 12, md: 4}}>
                            <FormControl fullWidth error={!!errors.phoneNumberCountryCode}>
                                <InputLabel required>{t("parent.country")}</InputLabel>
                                <Controller
                                    name="phoneNumberCountryCode"
                                    control={control}
                                    render={({field}) => (
                                        <Autocomplete
                                            {...field}
                                            value={phoneCodeOnlyOptions.find((opt) => opt.value === field.value) || phoneCodeOnlyOptions.find((opt) => opt.country === field.value) || null}
                                            onChange={(event, newValue) => {
                                                field.onChange(newValue?.value || null);
                                            }}
                                            getOptionKey={(option) => option.value}
                                            isOptionEqualToValue={(option, value) => option.value === value.value}
                                            filterOptions={(options, {inputValue}) => options
                                                .filter((option) => option.label.toLowerCase().includes(inputValue.toLowerCase()))}
                                            disablePortal
                                            options={phoneCodeOnlyOptions}
                                            renderInput={(params) => (
                                                <TextField {...params} placeholder={t("parent.placeholders.country")} />
                                            )}
                                        />
                                    )}
                                />
                                {errors.phoneNumberCountryCode?.message ? (
                                    <FormHelperText>{errors.phoneNumberCountryCode.message}</FormHelperText>
                                ) : null}
                            </FormControl>
                        </Grid>
                        <Grid item size={{xs: 12, md: 8}}>
                            <Controller
                                name="phoneNumber"
                                control={control}
                                render={({field}) => (
                                    <TextField
                                        {...field}
                                        onChange={(e) => field.onChange(e.target.value.replace(/\D/g, ""))}
                                        slotProps={{htmlInput: {required: false}}}
                                        fullWidth
                                        label={t("parent.contactNumber")}
                                        placeholder={t("parent.placeholders.contactNumber")}
                                        required
                                        error={!!errors.phoneNumber}
                                        helperText={errors.phoneNumber?.message}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item size={12}>
                            <FormControl fullWidth error={!!errors.onBehalfOfAnOrganisation}>
                                <InputLabel required>{t("parent.organizationQuestion")}</InputLabel>
                                <Controller
                                    name="onBehalfOfAnOrganisation"
                                    control={control}
                                    render={({field}) => (
                                        <RadioGroup {...field} row>
                                            <FormControlLabel
                                                value="true"
                                                control={<Radio />}
                                                label={t("parent.yes")}
                                            />
                                            <FormControlLabel
                                                value="false"
                                                control={<Radio />}
                                                label={t("parent.no")}
                                            />
                                        </RadioGroup>
                                    )}
                                />
                            </FormControl>
                        </Grid>

                        <Grid item size={12}>
                            <Controller
                                name="optedIntoMarketing"
                                control={control}
                                render={({field}) => (
                                    <FormControlLabel
                                        control={<Checkbox {...field} checked={field.value} />}
                                        label={t("parent.keepUpdated")}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item size={12} className="mt-4">
                            {isEdit ? (
                                <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2}}>
                                    <Button
                                        variant="back"
                                        onClick={handleBack}
                                        startIcon={<PlayArrowIcon className="rotate-180" fontSize="large" />}
                                    >
                                        {t("button.back")}
                                    </Button>
                                    <Button
                                        variant="accent"
                                        type="submit"
                                        loading={isPendingLeadRequest}
                                    >
                                        {t("button.nextStep")}
                                    </Button>
                                </Box>
                            ) : (
                                <Button
                                    variant="accent"
                                    type="submit"
                                    className="w-full"
                                    loading={isPendingLeadRequest}
                                >
                                    {t("button.nextStep")}
                                </Button>
                            )}
                        </Grid>
                    </Grid>
                </form>
            </Preloader>
        </div>
    );
};

export default ParentPage;
